import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SecondaryTextTypography } from '../../../../../App.styles';
import { useThemeContext } from '../../../../../global-state/themeContext';
import { useStoreInformation } from '../../../../../hooks';
import { Typography } from '../../../../../stories/atoms';
import {
  ColumnFlexContainer,
  Divider,
  RowFlexContainer,
} from '../../../../../stories/atoms/containerV2/ContainerV2';
import { storeOverviewTranslations } from '../../../../../translations';
import { formatAmountWithSeparator } from '../../../../../utils';
import { LayoutDetails, MobileChipLayout } from '../mobileChipLayout/MobileChipLayout';
import { StyledIndexTypography } from '../mobileChipLayout/MobileChipLayout.styles';
import { getIndexStatus, SalesAggregatedRow } from '../sales.util';

type SalesSummaryCardProps = {
  data: SalesAggregatedRow;
  isTotal: boolean;
  onClick: (data: SalesAggregatedRow) => void;
};

type SalesSummarySliderProps = {
  data: SalesAggregatedRow | null;
  subText: string;
};

export const SalesSummaryCard: FC<SalesSummaryCardProps> = ({ data, isTotal, onClick }) => {
  const { mode } = useThemeContext();
  const { metric, sob, indexLastYear, lastYearVsArea, lastYearVsSalesMarket } = data;
  const { corporateBrandId } = useStoreInformation();
  const isHMBrand = corporateBrandId === 0;

  const sendCardDetails = () => {
    onClick(data);
  };

  return (
    <RowFlexContainer
      hasBorder
      borderRadius={[3]}
      padding={[1, 3]}
      gap={[2]}
      alignItems="center"
      cursor="pointer"
      data-testid="sales-summary-card"
      backgroundColor={isTotal ? (mode === 'dark' ? 'backgroundSelectedHover' : 'baseGray') : 'inherit'}
      onClick={sendCardDetails}
    >
      <ColumnFlexContainer flex={1} gap={[1]}>
        <Typography type={isTotal ? 'body3' : 'body2'}>{metric}</Typography>
        <RowFlexContainer gap={[2]}>
          <StyledIndexTypography type="caption" status={getIndexStatus(indexLastYear)}>
            vs LY: {indexLastYear}
          </StyledIndexTypography>
          <SecondaryTextTypography type="caption">/</SecondaryTextTypography>
          <StyledIndexTypography type="caption" status={getIndexStatus(lastYearVsArea)}>
            vs Area: {lastYearVsArea}
          </StyledIndexTypography>
          {isHMBrand && (
            <>
              <SecondaryTextTypography type="caption">/</SecondaryTextTypography>
              <StyledIndexTypography type="caption" status={getIndexStatus(lastYearVsSalesMarket)}>
                vs SM: {lastYearVsSalesMarket}
              </StyledIndexTypography>
            </>
          )}
        </RowFlexContainer>
      </ColumnFlexContainer>
      <ColumnFlexContainer gap={[1]} alignItems="center">
        <Typography type="body4">{formatAmountWithSeparator(sob, '%', 1)}</Typography>
        <Typography>SoB</Typography>
      </ColumnFlexContainer>
    </RowFlexContainer>
  );
};

export const SalesSummarySlider: FC<SalesSummarySliderProps> = ({ data, subText }) => {
  const { t } = useTranslation();

  const { corporateBrandId } = useStoreInformation();
  const isHMBrand = corporateBrandId === 0;

  if (!data) return null;

  const {
    metric,
    salesNetThisYear,
    differenceLastYear,
    sob,
    drivingUnit,
    indexLastYear,
    lastYearVsArea,
    lastYearVsSalesMarket,
  } = data;

  const salesMarketItem: LayoutDetails[] = [
    { label: 'vs SM', value: lastYearVsSalesMarket.toString(), status: getIndexStatus(lastYearVsSalesMarket) },
  ];

  return (
    <ColumnFlexContainer padding={[2]}>
      <Typography type="h4">{metric}</Typography>
      <Typography>{subText}</Typography>
      <Divider margin={[2, 0, 0]} />
      <ColumnFlexContainer padding={[2, 0]}>
        <Typography>
          {t(
            storeOverviewTranslations.salesNetThisYear.key,
            storeOverviewTranslations.salesNetThisYear.defaultValue,
          )}
        </Typography>
        <Typography type="h3">{formatAmountWithSeparator(salesNetThisYear, null, 0)}</Typography>
      </ColumnFlexContainer>
      <MobileChipLayout
        layout="grid"
        gridColumns={2}
        layoutData={[
          { label: 'Diff LY', value: formatAmountWithSeparator(differenceLastYear), status: 'default' },
          { label: 'SoB%', value: formatAmountWithSeparator(sob, '%', 1), status: 'default' },
          { label: 'vs LY', value: indexLastYear.toString(), status: getIndexStatus(indexLastYear) },
          { label: 'DU', value: formatAmountWithSeparator(drivingUnit, '%', 1), status: 'default' },
          { label: 'vs Area', value: lastYearVsArea.toString(), status: getIndexStatus(lastYearVsArea) },
          ...(isHMBrand ? salesMarketItem : []),
        ]}
      />
    </ColumnFlexContainer>
  );
};
